import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetPatientDetails } from "../../../utils/apiCalls/patients";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

// Persisted across navigating away to a patient's detail view and back, so
// the search/page the user had isn't silently reset on return.
const SEARCH_KEY = "patientTableSearch";
const PAGE_KEY = "patientTablePage";

const formatDuration = (durationString) => {
  if (!durationString || durationString === "-") return "-";
  // Format: "X sessions - Y hours-Z minutes-W seconds" -> "X Ses - Y H - Z M - W Sec"
  return durationString
    .replace(/sessions/g, "Ses")
    .replace(/hours/g, "H")
    .replace(/minutes/g, "M")
    .replace(/seconds/g, "Sec")
    .replace(/ - /g, " - ");
};

export default function usePatientTable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(PAGE_KEY)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [totalRows, setTotalRows] = useState(0);


  const fetchPatientDetails = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString,
      };
      try {
        const response = await GetPatientDetails(payload);
        if (response.statusCode === 200) {
          setRows(response.data);
          setTotalRows(response.totalRecords);
        } else {
          console.error(
            "Error fetching professional details:",
            response.message,
          );
          setRows([]);
          setFetchError(response.message || "Failed to load patients");
        }
      } catch (error) {
        console.error("Error fetching professional details:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load patients");
      }
      setIsLoading(false);
    },
    [],
  );

  const retryFetchPatients = () => {
    fetchPatientDetails(page, rowsPerPage, debouncedSearchQuery);
  };

  const handleView = (row) => {
    sessionStorage.setItem("rowData", JSON.stringify(row));
    sessionStorage.setItem("patientName", row.name);
    sessionStorage.setItem("deviceId", row.deviceId);
    navigate("/patient-view", {
      state: { rowData: row, patientName: row.name },
    });
  };

  useEffect(() => {
    fetchPatientDetails(page, rowsPerPage, debouncedSearchQuery);
  }, [fetchPatientDetails, debouncedSearchQuery, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(PAGE_KEY, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    sessionStorage.setItem(SEARCH_KEY, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const handleForm = () => {
    // Add Patient navigates with no state, so usePatientForm falls back to
    // sessionStorage's "rowData" — clear it or a previously viewed/edited
    // patient's data leaks into what should be a blank add form.
    sessionStorage.removeItem("rowData");
    navigate("/patient-form");
  };

  return {
    rows,
    page,
    rowsPerPage,
    searchQuery,
    isLoading,
    fetchError,
    totalRows,
    handleView,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleForm,
    formatDuration,
    retryFetchPatients,
  };
}
